import { AppLayout } from '@/components/layout/AppLayout';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  User,
  Wallet,
  Bell,
  Lock,
  Settings,
  ChevronRight,
  Download,
  Upload,
  FileText,
  HelpCircle,
} from 'lucide-react';

export default function SettingsPage() {
  const settingSections = [
    {
      id: 'profile',
      name: 'Profile',
      description: 'Manage your personal information and contact details',
      icon: User,
      href: '/settings/profile',
    },
    {
      id: 'integrations',
      name: 'Integrations',
      description: 'Connect GitHub and other services to sync tasks',
      icon: '🔗',
      href: '/settings/integrations',
    },
    {
      id: 'billing',
      name: 'Billing & Rates',
      description: 'Configure your hourly rates and billing preferences',
      icon: Wallet,
      href: '/settings/billing',
    },
    {
      id: 'notifications',
      name: 'Notifications',
      description: 'Customize your notification preferences and alerts',
      icon: Bell,
      href: '/settings/notifications',
    },
    {
      id: 'security',
      name: 'Security',
      description: 'Manage your account security and privacy settings',
      icon: Lock,
      href: '/settings/security',
    },
    {
      id: 'preferences',
      name: 'App Preferences',
      description: 'Customize your workspace and app behavior',
      icon: Settings,
      href: '/settings/preferences',
    },
  ];

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-2">Settings</h1>
          <p className="text-muted-foreground">
            Manage your account preferences, billing, and application settings
          </p>
        </div>

        {/* Settings Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {settingSections.map((section) => {
            const Icon = section.icon;
            return (
              <Link key={section.id} to={section.href}>
                <Card className="border border-border rounded-lg p-4 border-0 hover:bg-muted/50 transition-all duration-200 cursor-pointer group">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
                          {section.name}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{section.description}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Quick Actions */}
        <Card className="border border-border rounded-lg p-4 border-0">
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button variant="outline" className="h-auto p-4 justify-start">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-3">
                  <Download className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="text-left">
                  <div className="font-medium text-foreground">Export Data</div>
                  <div className="text-sm text-muted-foreground">Download your account data</div>
                </div>
              </Button>

              <Button variant="outline" className="h-auto p-4 justify-start">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mr-3">
                  <Upload className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="text-left">
                  <div className="font-medium text-foreground">Import Data</div>
                  <div className="text-sm text-muted-foreground">Import from other tools</div>
                </div>
              </Button>

              <Button variant="outline" className="h-auto p-4 justify-start">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mr-3">
                  <FileText className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="text-left">
                  <div className="font-medium text-foreground">Backup Settings</div>
                  <div className="text-sm text-muted-foreground">Create a backup of your settings</div>
                </div>
              </Button>

              <Button variant="outline" className="h-auto p-4 justify-start">
                <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center mr-3">
                  <HelpCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="text-left">
                  <div className="font-medium text-foreground">Get Help</div>
                  <div className="text-sm text-muted-foreground">Contact support or view docs</div>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Account Information */}
        <Card className="border border-border rounded-lg p-4 border-0 mt-6">
          <CardHeader>
            <CardTitle className="text-lg">Account Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">Account Type</span>
                <span className="text-foreground font-medium">Professional</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">Member Since</span>
                <span className="text-foreground font-medium">January 2025</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">Storage Used</span>
                <span className="text-foreground font-medium">2.3 GB / 100 GB</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">API Calls This Month</span>
                <span className="text-foreground font-medium">1,247 / 10,000</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
